/**
 * useDataProcessing - Custom hook for surebet data processing
 * 
 * Provides memoized utilities and computed values for surebet data
 * Optimized for performance with useMemo and useCallback
 */

import { useMemo, useCallback } from 'react';
import {
  calculateMargin,
  isValidMargin,
  isProfitableMargin,
  getMaxMargin,
  getMaxMarginType,
  formatMarginPercentage,
  isValidCoefficient,
  formatCoefficient,
  getBookmakerName,
  getBestCoefficient,
  groupCoefficientsByType,
  getMarginTypeName,
  getOutcomeName,
  getAvailableMarginTypes,
  sortSurebets,
  filterByBookmakers,
  filterBySport,
  filterByChampionship,
  filterByMinMargin,
  filterByProfitable,
  formatDate,
  formatTime,
  formatEventDateTime,
  isToday,
  getTimeUntilEvent,
  calculateStatistics,
  groupBySport,
  groupByChampionship,
  isValidSurebet,
  sanitizeSurebet,
} from '../utils/surebetUtils';

/**
 * Main data processing hook
 * 
 * @param {Array<Object>} surebets - Array of surebet items
 * @param {Object} options - Processing options
 * @returns {Object} Processed data and utility functions
 */
export const useDataProcessing = (surebets = [], options = {}) => {
  const {
    language = 'bg',
    sortBy = 'margin',
    sortOrder = 'desc',
    filters = {},
  } = options;

  /**
   * Memoized filtered surebets
   */
  const filteredSurebets = useMemo(() => {
    let filtered = [...surebets];

    // Apply bookmaker filter
    if (filters.bookmakers && filters.bookmakers.length > 0) {
      filtered = filterByBookmakers(filtered, filters.bookmakers);
    }

    // Apply sport filter
    if (filters.sport) {
      filtered = filterBySport(filtered, filters.sport);
    }

    // Apply championship filter
    if (filters.championship) {
      filtered = filterByChampionship(filtered, filters.championship);
    }

    // Apply minimum margin filter
    if (filters.minMargin !== undefined) {
      filtered = filterByMinMargin(filtered, filters.minMargin);
    }

    // Apply profitable filter
    if (filters.profitableOnly) {
      filtered = filterByProfitable(filtered, true);
    }

    return filtered;
  }, [surebets, filters]);

  /**
   * Memoized sorted surebets
   */
  const sortedSurebets = useMemo(() => {
    return sortSurebets(filteredSurebets, sortBy, sortOrder);
  }, [filteredSurebets, sortBy, sortOrder]);

  /**
   * Memoized statistics
   */
  const statistics = useMemo(() => {
    return calculateStatistics(sortedSurebets);
  }, [sortedSurebets]);

  /**
   * Memoized grouping by sport
   */
  const groupedBySport = useMemo(() => {
    return groupBySport(sortedSurebets);
  }, [sortedSurebets]);

  /**
   * Memoized grouping by championship
   */
  const groupedByChampionship = useMemo(() => {
    return groupByChampionship(sortedSurebets);
  }, [sortedSurebets]);

  /**
   * Get margin data for a specific surebet
   */
  const getMarginData = useCallback((surebet) => {
    if (!surebet) return null;

    return {
      maxMargin: getMaxMargin(surebet),
      maxMarginType: getMaxMarginType(surebet),
      availableTypes: getAvailableMarginTypes(surebet),
      isProfitable: surebet.marginFlag,
    };
  }, []);

  /**
   * Format margin for display
   */
  const formatMargin = useCallback((margin, decimals = 2) => {
    return formatMarginPercentage(margin, decimals);
  }, []);

  /**
   * Format coefficient for display
   */
  const formatCoef = useCallback((coefficient, decimals = 2) => {
    return formatCoefficient(coefficient, decimals);
  }, []);

  /**
   * Get localized margin type name
   */
  const getLocalizedMarginType = useCallback((marginKey) => {
    return getMarginTypeName(marginKey, language);
  }, [language]);

  /**
   * Get localized outcome name
   */
  const getLocalizedOutcome = useCallback((marginKey, outcomeKey) => {
    return getOutcomeName(marginKey, outcomeKey, language);
  }, [language]);

  /**
   * Format event date/time
   */
  const formatEventDate = useCallback((event) => {
    return formatEventDateTime(event, language);
  }, [language]);

  /**
   * Check if event is today
   */
  const checkIsToday = useCallback((dateString) => {
    return isToday(dateString);
  }, []);

  /**
   * Get time until event starts
   */
  const getTimeUntil = useCallback((dateString, timeString) => {
    return getTimeUntilEvent(dateString, timeString);
  }, []);

  /**
   * Validate surebet
   */
  const validateSurebet = useCallback((surebet) => {
    return isValidSurebet(surebet);
  }, []);

  /**
   * Sanitize surebet data
   */
  const sanitize = useCallback((surebet) => {
    return sanitizeSurebet(surebet);
  }, []);

  /**
   * Get best coefficient from a list
   */
  const findBestCoefficient = useCallback((coefficients) => {
    return getBestCoefficient(coefficients);
  }, []);

  /**
   * Group coefficients by type
   */
  const groupCoefficients = useCallback((coefficients) => {
    return groupCoefficientsByType(coefficients);
  }, []);

  /**
   * Get bookmaker display name
   */
  const getBookmakerDisplayName = useCallback((bookmakerId) => {
    return getBookmakerName(bookmakerId);
  }, []);

  /**
   * Calculate margin from coefficient values
   */
  const calculateMarginValue = useCallback((coefficients) => {
    return calculateMargin(coefficients);
  }, []);

  /**
   * Check if margin is valid
   */
  const checkValidMargin = useCallback((margin) => {
    return isValidMargin(margin);
  }, []);

  /**
   * Check if margin is profitable
   */
  const checkProfitableMargin = useCallback((margin) => {
    return isProfitableMargin(margin);
  }, []);

  /**
   * Check if coefficient is valid
   */
  const checkValidCoefficient = useCallback((coefficient) => {
    return isValidCoefficient(coefficient);
  }, []);

  /**
   * Format date
   */
  const formatDateOnly = useCallback((dateString) => {
    return formatDate(dateString, language);
  }, [language]);

  /**
   * Format time
   */
  const formatTimeOnly = useCallback((timeString) => {
    return formatTime(timeString);
  }, []);

  return {
    // Processed data
    surebets: sortedSurebets,
    filteredCount: filteredSurebets.length,
    totalCount: surebets.length,
    statistics,
    groupedBySport,
    groupedByChampionship,

    // Margin utilities
    getMarginData,
    formatMargin,
    calculateMarginValue,
    checkValidMargin,
    checkProfitableMargin,

    // Coefficient utilities
    formatCoef,
    findBestCoefficient,
    groupCoefficients,
    getBookmakerDisplayName,
    checkValidCoefficient,

    // Margin type utilities
    getLocalizedMarginType,
    getLocalizedOutcome,

    // Date/time utilities
    formatEventDate,
    formatDateOnly,
    formatTimeOnly,
    checkIsToday,
    getTimeUntil,

    // Validation utilities
    validateSurebet,
    sanitize,
  };
};

/**
 * Hook for margin calculations
 * 
 * @param {Object} surebet - Surebet item
 * @returns {Object} Margin data and utilities
 */
export const useMarginData = (surebet) => {
  const maxMargin = useMemo(() => getMaxMargin(surebet), [surebet]);
  const maxMarginType = useMemo(() => getMaxMarginType(surebet), [surebet]);
  const availableTypes = useMemo(() => getAvailableMarginTypes(surebet), [surebet]);

  const isProfitable = useMemo(() => {
    return surebet?.marginFlag === true;
  }, [surebet]);

  return {
    maxMargin,
    maxMarginType,
    availableTypes,
    isProfitable,
  };
};

/**
 * Hook for event data formatting
 * 
 * @param {Object} event - Event object
 * @param {string} language - Language code
 * @returns {Object} Formatted event data
 */
export const useEventData = (event, language = 'bg') => {
  const formattedDate = useMemo(() => {
    return formatDate(event?.date, language);
  }, [event, language]);

  const formattedTime = useMemo(() => {
    return formatTime(event?.time);
  }, [event]);

  const formattedDateTime = useMemo(() => {
    return formatEventDateTime(event, language);
  }, [event, language]);

  const eventIsToday = useMemo(() => {
    return isToday(event?.date);
  }, [event]);

  const timeUntil = useMemo(() => {
    return getTimeUntilEvent(event?.date, event?.time);
  }, [event]);

  return {
    formattedDate,
    formattedTime,
    formattedDateTime,
    isToday: eventIsToday,
    timeUntil,
  };
};

/**
 * Hook for statistics
 * 
 * @param {Array<Object>} surebets - Array of surebet items
 * @returns {Object} Calculated statistics
 */
export const useStatistics = (surebets = []) => {
  return useMemo(() => {
    return calculateStatistics(surebets);
  }, [surebets]);
};

/**
 * Hook for grouping data
 * 
 * @param {Array<Object>} surebets - Array of surebet items
 * @param {string} groupBy - Group by field ('sport' or 'championship')
 * @returns {Object} Grouped surebets
 */
export const useGrouping = (surebets = [], groupBy = 'sport') => {
  return useMemo(() => {
    if (groupBy === 'sport') {
      return groupBySport(surebets);
    } else if (groupBy === 'championship') {
      return groupByChampionship(surebets);
    }
    return {};
  }, [surebets, groupBy]);
};

export default useDataProcessing;

